import React, { useContext } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { WorkspaceContext } from '@/contexts/WorkspaceContext';
import { useModuleAccess } from '@/hooks/useModuleAccess';

interface ProtectedRouteProps {
  children: React.ReactNode;
  resource?: string; // Module ID to check access for
  permission?: 'view' | 'edit' | 'create' | 'delete' | 'admin'; // Permission action to check
}

export function ProtectedRoute({ children, resource, permission = 'view' }: ProtectedRouteProps) {
  const { user } = useAuth();
  const workspaceContext = useContext(WorkspaceContext);
  
  // Always call useModuleAccess hook (Rules of Hooks)
  // Pass undefined if no workspace, hook will handle it gracefully
  const moduleAccess = useModuleAccess(
    'workspace', 
    workspaceContext?.currentWorkspace?.id
  );

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  // WorkspaceContext is optional - only check if available
  if (workspaceContext?.isLoading) {
    return <div className="p-4">Loading workspace...</div>;
  }
  
  // If resource is specified, check module access
  if (resource && workspaceContext?.currentWorkspace) {
    // Check if user has access to the required module/permission
    const hasAccess = permission === 'view' 
      ? moduleAccess.hasModule(resource)
      : moduleAccess.hasPermission(resource, permission);

    if (!hasAccess) {
    return (
        <div className="flex flex-col items-center justify-center min-h-[400px] p-6">
          <div className="text-center space-y-4 max-w-md">
            <div className="text-4xl">🔒</div>
            <h2 className="text-2xl font-bold">Access Restricted</h2>
            <p className="text-muted-foreground">
              You don't have access to this module. Please contact your workspace administrator or upgrade your plan.
            </p>
            {moduleAccess.planCode === 'trial' && (
              <div className="mt-4 p-4 bg-primary/10 border border-primary/20 rounded-lg">
                <p className="text-sm text-foreground mb-2">
                  Upgrade to unlock this feature and more!
                </p>
                <button
                  onClick={() => window.location.href = `/w/${workspaceContext.currentWorkspace?.id}/dashboard/billing`}
                  className="text-sm text-primary hover:underline font-medium"
                >
                  Upgrade Now →
                </button>
              </div>
            )}
            </div>
      </div>
    );
    }
  }

  return <>{children}</>;
}

export default ProtectedRoute;
